import { useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { Pencil } from 'lucide-react';
import { Button } from '../../components/Button';
import { Card, Page } from '../../components/Page';
import { useDb } from '../../db/hooks';
import { useCanWrite } from '../../account/useCanWrite';
import { deleteReport, useLabCatalog, useLabReports, useLabResults, type LabResult } from '../../data/labs';
import { describeRange } from '../../lib/labs';
import { Change, groupByPanel, LabStatusBadge, panelName, previousResult, reportUrr, resultText } from './shared';

/** One report: results by panel with flags and the change since the previous report. */
export function LabReportPage() {
  const { id } = useParams();
  const db = useDb();
  const navigate = useNavigate();
  const canWrite = useCanWrite();
  const { tests, isLoading: testsLoading } = useLabCatalog();
  const { reports, isLoading } = useLabReports();
  const { results, isLoading: resultsLoading } = useLabResults();
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (isLoading || resultsLoading || testsLoading) return null;
  const report = reports.find((item) => item.id === id);
  if (!report) return <Navigate to="/labs" replace />;

  const own = results.filter((result) => result.report_id === report.id);
  const byTest = new Map(own.map((result) => [result.test_id, result]));
  // Results whose test was deleted from the catalog still show, under their saved name.
  const orphans = own.filter((result) => !tests.some((test) => test.id === result.test_id));
  const groups = groupByPanel(tests, { includeHidden: true })
    .map((group) => ({ ...group, tests: group.tests.filter((test) => byTest.has(test.id)) }))
    .filter((group) => group.tests.length);
  const ratio = reportUrr(own, tests);

  const row = (result: LabResult, name: string, range: string | null) => (
    <li key={result.id} className="flex items-start gap-3 py-2.5">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{name}</p>
        <p className="text-xs text-clinical-muted">{range ? `Range ${range}` : null}</p>
      </div>
      <div className="text-right">
        <p className="text-sm font-semibold tabular-nums">
          {resultText(result)} <span className="text-xs font-normal text-clinical-muted">{result.unit}</span>
        </p>
        <div className="flex flex-col items-end gap-1">
          <LabStatusBadge status={result.status} />
          <Change current={result} previous={previousResult(results, result.test_id, report.result_date)} />
        </div>
      </div>
    </li>
  );

  return (
    <Page
      title={format(parseISO(report.result_date), 'd MMMM yyyy')}
      back="/labs"
      actions={
        canWrite ? (
          <Link to={`/labs/${report.id}/edit`} className="focus-ring inline-flex min-h-9 items-center gap-1 rounded-lg border border-clinical-line px-3 text-sm font-semibold hover:bg-clinical-surface">
            <Pencil className="h-4 w-4" /> Edit
          </Link>
        ) : null
      }
    >
      {report.lab_name || report.notes ? (
        <Card>
          {report.lab_name ? <p className="text-sm font-medium">{report.lab_name}</p> : null}
          {report.notes ? <p className="whitespace-pre-line text-sm text-clinical-muted">{report.notes}</p> : null}
        </Card>
      ) : null}

      {groups.map((group) => (
        <Card key={group.panel} title={panelName(group.panel)}>
          <ul className="divide-y divide-clinical-line">
            {group.tests.map((test) => row(byTest.get(test.id)!, test.name, describeRange(test)))}
            {group.panel === 'RFT' && ratio != null ? (
              <li className="flex items-center gap-3 py-2.5">
                <div className="flex-1">
                  <p className="text-sm font-medium">URR</p>
                  <p className="text-xs text-clinical-muted">Worked out from the urea before and after dialysis</p>
                </div>
                <p className="text-sm font-semibold tabular-nums">{ratio}%</p>
              </li>
            ) : null}
          </ul>
        </Card>
      ))}
      {orphans.length ? (
        <Card title="Other results">
          <ul className="divide-y divide-clinical-line">{orphans.map((result) => row(result, result.test_name_snapshot, null))}</ul>
        </Card>
      ) : null}

      {canWrite ? (
        confirmDelete ? (
          <div className="flex items-center gap-2">
            <span className="flex-1 text-sm">Delete this report and its results?</span>
            <Button variant="danger" onClick={() => void deleteReport(db, report.id, own).then(() => navigate('/labs'))}>
              Delete
            </Button>
            <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
          </div>
        ) : (
          <Button variant="ghost" className="w-full !text-clinical-danger hover:!bg-red-500/10" onClick={() => setConfirmDelete(true)}>
            Delete report
          </Button>
        )
      ) : null}
    </Page>
  );
}
